import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2020 semantics GmbH
//  Date      : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 156393 $
//  Purpose   : statistic parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Icon, Table } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import log from 'uix/logging';
import { keys, values, mapItems, dict, isNil, isFunction, flatten } from 'ldash';
import { useSelector, useState, usePartContext, useApi, useSummary, useTimestats } from 'uix/hooks';
import { HeaderSegment, SplitPane, XMenu, InView, TranslatedTable, ResizableDialog, PortalTarget } from 'uix/components';
import { formatSI, prettyBytes } from 'uix/utils';
import { DomainLabel } from 'uix/domain/label';
import { TypeDescription, DatatypeDescription, TreeNode } from 'uix/types/components';
import { StateDescription } from 'uix/states/components';
import { useStatusTypes } from 'uix/states/hooks';
import { interval2granularity } from 'uix/utils/date';
import { MinorBottomTabGroupSegment } from 'uix/tree/tabs';
// ---Local imports
import { Plot, formatDateWithGranularity } from './plots';
import { DomainTreeStats } from './components';
import { TIMESTATS_COLSPEC, FILTER_TIMESTATS_ROWS } from './timestats';
// ---Exports
const get_i18n_row = (itypes, i18n, summaryName, row)=>{
    if (summaryName == 'datatype') {
        return i18n.get(`types_datatype_${itypes.datatypeMap[row]}`);
    }
    return `${summaryName}_${row}`;
};
const SummaryRowCell0 = ({ name, value, other })=>{
    /* first column in summary stats table
  value: cell value
  */ let itypes = useSelector(Sel.itypes);
    let { data: domains } = useSelector(Sel.domains);
    let { i18n } = usePartContext();
    if (name == 'domain') {
        return /*#__PURE__*/ _jsx(DomainLabel, {
            odid: value
        });
    }
    if (name == 'structure' || itypes.datatypeMapInverse[name]) {
        let s = name == 'structure' ? 'substructure' : name;
        let dt = itypes.datatypeMapInverse[s];
        return /*#__PURE__*/ _jsx(TypeDescription, {
            datatype: dt,
            subtype: value
        });
    }
    if (name == 'datatype') {
        return /*#__PURE__*/ _jsx(DatatypeDescription, {
            datatype: value
        });
    }
    if (name == 'archive_state') {
        return i18n.span(`archivestate_${value}`);
    }
    if (name == 'archive_store') {
        return /*#__PURE__*/ _jsx("span", {
            children: other.archivestore
        });
    }
    if (name == 'metadata_origin') {
        return i18n.span(`xmlorigin_${value}`);
    }
    if (name.endsWith('state')) {
        if (value == '') return 'Leer';
        return /*#__PURE__*/ _jsx(StateDescription, {
            statusType: name.slice(0, -5),
            statusValue: value,
            icon: {
                width: 16,
                verticalAlign: 'middle'
            }
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Icon, {
                name: "folder outline"
            }),
            " ",
            get_i18n_row(itypes, i18n, name, value)
        ]
    });
};
const render_value = (value, value_type, fmtSi)=>{
    if (isNil(value)) {
        return '';
    }
    if (value_type == 'filesize') {
        return prettyBytes(value);
    } else {
        return fmtSi ? formatSI(value, 1) : value.toLocaleString();
    }
};
const NavList = ({ name, row, col })=>{
    let { data } = useApi('getStatsNavlist', {
        name: name + '_summary',
        row,
        col
    });
    let nodes = data?.nodes || [];
    let tnodes = nodes.map((n)=>/*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(TreeNode, {
                node: n,
                link: true,
                linkLazy: true
            })
        }, n.otid));
    return /*#__PURE__*/ _jsx("div", {
        children: tnodes
    });
};
const navlistLink = ({ name, rowname, colname, rendered_value })=>{
    let [open, setOpen] = useState(false);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            open && /*#__PURE__*/ _jsxs(ResizableDialog, {
                open: true,
                onClose: ()=>setOpen(false),
                children: [
                    /*#__PURE__*/ _jsx(NavList, {
                        name: name,
                        row: rowname,
                        col: colname
                    }),
                    ' '
                ]
            }),
            /*#__PURE__*/ _jsx("span", {
                style: {
                    cursor: 'pointer'
                },
                onClick: ()=>setOpen(!open),
                children: rendered_value
            })
        ]
    });
};
let get_i18n_head = (i18n, colname)=>{
    return i18n.get(colname);
};
const SummaryTable = ({ name })=>{
    let [fmtSi, setFmtSi] = useState(true);
    let { dname, i18n } = usePartContext();
    let { sumdata, columns, navlistCols, loading, reload } = useSummary(name, dname);
    let { filled: cols, types: coltypes } = columns;
    let menuPortal = `summary_${name}`;
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: {
                    name: 'refresh',
                    loading
                },
                onClick: reload
            }),
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                content: "SI",
                value: fmtSi,
                set: setFmtSi
            }),
            /*#__PURE__*/ _jsx(PortalTarget, {
                id: menuPortal
            })
        ]
    });
    let cell_renderers = dict(navlistCols.map((colname)=>[
            colname,
            (colname, rowname, rendered_value)=>navlistLink({
                    name,
                    colname,
                    rowname,
                    rendered_value
                })
        ]));
    let tdata = mapItems(sumdata, (row, data)=>({
            rowtype: row,
            ...data
        }));
    let tcols = cols.map((colname, i)=>({
            id: colname,
            accessor: colname,
            Header: get_i18n_head(i18n, colname),
            Cell: cell_renderers[colname] ? ({ value, original })=>cell_renderers[colname](colname, original.rowtype, render_value(value, coltypes[colname], fmtSi)) : ({ value })=>render_value(value, coltypes[colname], fmtSi),
            selectable: true,
            autoselect: i < 8
        }));
    tcols = [
        {
            id: 'rowtype',
            accessor: 'rowtype',
            Header: 'Type',
            Cell: ({ value, original })=>/*#__PURE__*/ _jsx(SummaryRowCell0, {
                    name: name,
                    value: value,
                    other: original
                })
        },
        ...tcols
    ];
    // persistence versioning (persistKey) depends on columns
    // so we must not render empty cols and reset persisted data
    let colsKnown = tcols.length > 1;
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        basic: true,
        header: i18n.span(`${name}_summary`),
        menu: menu,
        children: colsKnown && /*#__PURE__*/ _jsx(TranslatedTable, {
            compact: true,
            data: tdata,
            columns: tcols,
            menuPortal: menuPortal,
            persistKey: `summary_${name}`
        })
    }, name);
};
const TimestatsTable = ({ name })=>{
    let [startDate, setStartDate] = useState('');
    let [fmtSi, setFmtSi] = useState(true);
    let [interval, setInterval] = useState('month');
    let { dname, i18n, dconfig } = usePartContext();
    let iconfig = useSelector(Sel.iconfig);
    let [tstype, datefield] = name.split('_');
    let { tsdata, dt_start, loading, reload } = useTimestats({
        tstype,
        datefield,
        interval,
        startDate
    });
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        children: [
            /*#__PURE__*/ _jsx(XMenu.Item, {
                icon: {
                    name: 'refresh',
                    loading
                },
                onClick: reload
            }),
            /*#__PURE__*/ _jsx(XMenu.Toggle, {
                content: "SI",
                value: fmtSi,
                set: setFmtSi
            }),
            /*#__PURE__*/ _jsx(XMenu.DateInput, {
                value: dt_start?.split('T')[0] || '',
                onValueChange: setStartDate,
                debounce: 500
            }),
            /*#__PURE__*/ _jsx(XMenu.Dropdown, {
                options: [
                    'day',
                    'week',
                    'month',
                    'quart'
                ].map((v)=>({
                        value: v,
                        key: v,
                        text: i18n.span(`timestats_interval_${v}`)
                    })),
                value: interval,
                onChange: (e, data)=>setInterval(data.value)
            })
        ]
    });
    let cols = TIMESTATS_COLSPEC[tstype] ?? TIMESTATS_COLSPEC.default;
    if (isFunction(cols.at(-1))) {
        let other_cols = cols.at(-1)({
            iconfig,
            dconfig,
            tsdata
        });
        cols = [
            ...cols.slice(0, -1),
            ...other_cols
        ];
    }
    let headers = cols.map(([colname, coli18n])=>/*#__PURE__*/ _jsx(Table.HeaderCell, {
            children: i18n.span(coli18n)
        }, colname));
    let filter_rows = FILTER_TIMESTATS_ROWS[name];
    if (filter_rows) {
        tsdata = tsdata.filter(filter_rows);
    }
    let rows = tsdata.map((dtrow)=>{
        let cells = cols.map(([colname, coli18n])=>/*#__PURE__*/ _jsx(Table.Cell, {
                collapsing: true,
                children: render_value(dtrow[colname], null, fmtSi)
            }, colname));
        return /*#__PURE__*/ _jsxs(Table.Row, {
            children: [
                /*#__PURE__*/ _jsx(Table.Cell, {
                    collapsing: true,
                    style: {
                        width: '15%'
                    },
                    children: formatDateWithGranularity(new Date(dtrow.datetime), interval2granularity[interval])
                }),
                cells
            ]
        }, dtrow.datetime);
    });
    return /*#__PURE__*/ _jsx(HeaderSegment, {
        basic: true,
        header: i18n.span(`timestats_${name}`),
        menu: menu,
        children: /*#__PURE__*/ _jsxs(Table, {
            celled: true,
            striped: true,
            compact: "very",
            children: [
                /*#__PURE__*/ _jsx(Table.Header, {
                    children: /*#__PURE__*/ _jsxs(Table.Row, {
                        children: [
                            /*#__PURE__*/ _jsx(Table.HeaderCell, {
                                children: i18n.span('date')
                            }),
                            headers
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Table.Body, {
                    children: rows
                })
            ]
        })
    }, name);
};
export const StatsTables = ({ dname, go, request, i18n })=>{
    // for triggering config load
    useStatusTypes();
    let { data } = useApi('filter/getStatsGroupingInfo', {}, {
        cached: true,
        autoreset: false
    });
    let _groups = data?.groups || [];
    let groups = dict(_groups.map((g)=>[
            g.name,
            g
        ]));
    let selectedGroup = request.id || 'overview';
    let menu = /*#__PURE__*/ _jsx(XMenu, {
        children: /*#__PURE__*/ _jsx(XMenu.Dropdown, {
            options: keys(groups).map((v)=>({
                    value: v,
                    key: v,
                    text: i18n.get(`qastatsgroup_${v}`)
                })),
            value: selectedGroup,
            onChange: (e, data)=>go({
                    id: data.value
                })
        })
    });
    let content = null;
    if (groups[selectedGroup]) {
        content = groups[selectedGroup].items.map((e)=>{
            if (e.type == 'summary') {
                return /*#__PURE__*/ _jsx(SummaryTable, {
                    name: e.name.replace('_summary', '')
                }, e.name);
            } else if (e.type == 'timestats') {
                return /*#__PURE__*/ _jsx(TimestatsTable, {
                    name: e.name
                }, e.name);
            }
        }).filter((e)=>e);
    }
    return /*#__PURE__*/ _jsx(TabGroupStats, {
        icon: "bar chart",
        header: "Details",
        menu: menu,
        children: content
    });
};
export const StatsPlots = ({})=>{
    let plots = keys(Plot.PLOTS).map((name)=>/*#__PURE__*/ _jsx(InView, {
            children: /*#__PURE__*/ _jsx(Plot, {
                flex: true,
                showMenu: true,
                showLegend: true,
                name: name
            })
        }, name));
    return /*#__PURE__*/ _jsx(TabGroupStats, {
        header: "Plots",
        icon: "chart bar",
        flexInner: false,
        children: /*#__PURE__*/ _jsx("div", {
            style: {
                height: `${plots.length * 300}px`
            },
            children: /*#__PURE__*/ _jsx(SplitPane, {
                flex: true,
                flexInner: true,
                borderless: true,
                children: plots
            })
        })
    });
};
export const StatsDomainTable = ({})=>{
    let subdomains = useSelector(Sel.subdomains);
    return /*#__PURE__*/ _jsx(TabGroupStats, {
        headerless: true,
        fitted: true,
        children: /*#__PURE__*/ _jsx(DomainTreeStats, {
            menuPortal: "StatsTabGroupPortal"
        })
    });
};
const TabGroupStats = ({ children, menu, icon, header, subheader, flexInner = true, ...props })=>{
    return /*#__PURE__*/ _jsx(MinorBottomTabGroupSegment, {
        header: header,
        subheader: subheader,
        flexInner: flexInner,
        overflowInner: true,
        icon: icon,
        menu: menu,
        style: {
            marginBottom: '0px',
            paddingBottom: '0px'
        },
        portal: "StatsTabGroupPortal",
        ...props,
        children: children
    });
};
